import type { ReactElement } from 'react';
import {
  BookOpen,
  CalendarDays,
  Compass,
  Contact,
  Cpu,
  FileStack,
  GaugeCircle,
  Handshake,
  Home,
  Landmark,
  ListChecks,
  ListTodo,
  ShieldCheck,
  Target,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { IconeMmHays } from '@/features/mm-hays/componentes/IconeMmHays';
import type { Permissao } from '@/tipos/api';

export interface ItemMenu {
  rotulo: string;
  /** O que a área faz, no tooltip do item (CONVENCOES-UI §8.1). Obrigatório: item sem dica não compila. */
  dica: string;
  para: string;
  /**
   * O ícone do item. Quase sempre um do lucide; o MM Hays usa o próprio rosto (`IconeMmHays`), porque é
   * uma persona e não uma função. Daí o tipo aceitar qualquer componente que receba `className`.
   */
  icone: LucideIcon | ((props: { className?: string }) => ReactElement);
  /**
   * Permissão que o item exige. **Ausente = todo autenticado vê** (hoje, só o Início).
   *
   * 🚨 Esconder aqui não substitui o `@Permissao()` do backend nem o `RequerPermissao` da rota: é a
   * camada de menor privilégio **visível**, para a pessoa não encarar um menu cheio de becos.
   */
  permissao?: Permissao;
  /** Cabeçalho sob o qual o item aparece; itens sem grupo vêm primeiro, soltos. */
  grupo?: string;
  /** Motivo pelo qual o item ainda não abre — vira `Tooltip` + `sr-only` no item desabilitado. */
  indisponivel?: string;
}

export const GRUPO_ADMINISTRACAO = 'Administração';

/**
 * Navegação do painel, declarativa.
 *
 * Clientes, bancos, propostas e documentos entram depois, cada um como um módulo com seu quarteto de
 * docs. O plano estratégico (iteração 7) abriu na 7.4.
 *
 * "Usuários" abriu na iteração 1.5, "Papéis e permissões" na 1.10, "Pessoas" na 2.3, "Listas de leads" e
 * "Tipos de atividade" na 10.2, "Leads" na 10.4, "Agenda" na 12.3, "Bases do MM Hays" na 13.4,
 * "MM Hays" na 13.7, "Operações de crédito" na 14.1, "Provedores" na 14.4 e "Ofertas de crédito" na 14.6.
 */
export const ITENS_MENU: readonly ItemMenu[] = [
  { rotulo: 'Início', dica: 'Visão geral e atalhos da plataforma', para: '/', icone: Home },
  /*
   * Solto, logo após o Início, fora de "Administração": é conteúdo de negócio, não gestão da plataforma
   * (decisão de 06/10/2026, README do módulo plano-estrategico §6).
   */
  {
    rotulo: 'Plano estratégico',
    dica: 'As fases, os riscos e os próximos passos da Tríplice',
    para: '/plano-estrategico',
    icone: Compass,
    permissao: 'plano.ver',
  },
  /*
   * Cadastro único de pessoas (iteração 2.3): também solto, fora de "Administração" — é o trabalho do dia a
   * dia, não gestão da plataforma. Vem depois do Plano, que tem a posição logo após o Início por decisão de
   * 06/10/2026 (README do plano-estrategico §6).
   */
  {
    rotulo: 'Pessoas',
    dica: 'Cadastro único de pessoas físicas e jurídicas, com contatos, endereços e relações',
    para: '/pessoas',
    icone: Contact,
    permissao: 'pessoas.ver',
  },
  /*
   * Leads (iteração 10.4): trabalho do dia a dia, solto, logo depois de Pessoas — o lead nasce sobre uma pessoa
   * (ADR 0020). Quem só tem `leads.ver` vê os seus; `leads.ver_todos`, os de todos.
   */
  {
    rotulo: 'Leads',
    dica: 'Quem procurou a Tríplice e ainda não virou oportunidade: cadastro, responsável e acompanhamento',
    para: '/leads',
    icone: Target,
    permissao: 'leads.ver',
  },
  /*
   * Agenda (iteração 12.3; ADR 0024): a semana de atividades de quem usa, de todos os registros — trabalho do dia
   * a dia, solta, logo depois de Leads. A de outra pessoa exige também `agenda.ver_equipe` (dentro da tela).
   */
  {
    rotulo: 'Agenda',
    dica: 'Seus compromissos e tarefas da semana, com vencidas, hoje e próximas',
    para: '/agenda',
    icone: CalendarDays,
    permissao: 'agenda.ver',
  },
  /*
   * Provedores (iteração 14.4; ADR 0039): o catálogo de **quem financia** cada operação de crédito — bancos,
   * FIDCs, gestoras, factorings, fintechs, multilaterais. Solto, no bloco do dia a dia: o analista abre aqui
   * para saber a quem levar a demanda. Fica **depois** da Agenda, que é a semana de quem está usando.
   *
   * 🚨 Diferente de "Operações de crédito", que fica em "Administração": aquilo é manutenção de catálogo
   * fechado (as 58 do guia), isto é trabalho. As duas telas são do mesmo módulo e da mesma permissão de
   * leitura, e é a **posição no menu** que diz qual é qual.
   */
  {
    rotulo: 'Provedores',
    dica: 'As instituições que financiam as operações de crédito: quem são, o que financiam e por qual porta se entra',
    para: '/provedores',
    icone: Landmark,
    permissao: 'provedores.ver',
  },
  /*
   * Ofertas (iteração 14.6): a central das fichas provedor × operação — **a tela mais usada do módulo**
   * (Fluxo §6). Fica ao lado de Provedores, e não dentro dele, porque é por aqui que se trabalha: a pergunta
   * do dia a dia é "quem financia isto, nestas condições", e não "o que esta instituição faz".
   */
  {
    rotulo: 'Ofertas de crédito',
    dica: 'Quem financia cada operação e em que condições: ticket, prazo, garantias, acessibilidade e o que falta confirmar',
    para: '/provedores/ofertas',
    icone: Handshake,
    permissao: 'provedores.ver',
  },
  /*
   * O painel do acervo (iteração 14.13). Fica **solto**, ao lado das ofertas, e não em "Administração":
   * é por onde o dia começa — cada número abre a lista filtrada, e é assim que a fila de trabalho aparece
   * (Fluxo §4). Chave própria: ver o acervo agregado é leitura de outra natureza que abrir uma ficha.
   */
  {
    rotulo: 'Painel de provedores',
    dica: 'Quanto do guia já tem resposta confiável, onde estão as lacunas e o que está parado. Cada número abre a lista correspondente',
    para: '/provedores/painel',
    icone: GaugeCircle,
    permissao: 'provedores.painel',
  },
  /*
   * MM Hays (iteração 13.7): o chat com o agente interno — trabalho do dia a dia, solto, não gestão da
   * plataforma (as *bases* é que são manutenção, e ficam em "Administração"). A chave `mm_hays.conversar` é de
   * concessão reservada ao master (ADR 0026): nenhum papel nasce com ela, nem o `admin`, então o item só
   * aparece para quem o master habilitou.
   */
  {
    rotulo: 'MM Hays',
    dica: 'Tire dúvidas com o assistente da Tríplice, com base no que a equipe publicou',
    para: '/mm-hays',
    // O rosto do agente, não um `Sparkles`: no menu, quem procura o MM Hays procura **ele** (11/10/2026).
    icone: IconeMmHays,
    permissao: 'mm_hays.conversar',
  },
  {
    rotulo: 'Usuários',
    dica: 'Quem acessa a plataforma: convites, papéis, desativação e sessões',
    para: '/usuarios',
    icone: Users,
    permissao: 'usuarios.ver',
    grupo: GRUPO_ADMINISTRACAO,
  },
  {
    rotulo: 'Papéis e permissões',
    dica: 'O que cada papel pode ver e fazer em cada módulo',
    para: '/papeis',
    icone: ShieldCheck,
    // `papeis.ver`, e não `papeis.gerir`: quem só pode olhar a matriz também precisa alcançar a tela (1.10).
    permissao: 'papeis.ver',
    grupo: GRUPO_ADMINISTRACAO,
  },
  /*
   * Bases do MM Hays (iteração 13.4): manutenção do que o agente sabe, não trabalho do dia a dia — por isso em
   * "Administração". A chave `mm_hays.bases_ver` é de concessão reservada ao master (ADR 0026): nenhum papel
   * nasce com ela, nem o `admin`, então o item só aparece para quem o master habilitou. O chat fica no item
   * "MM Hays", solto acima, com a sua própria chave.
   */
  {
    rotulo: 'Bases do MM Hays',
    dica: 'O que o MM Hays sabe: bases e documentos publicados pela equipe',
    para: '/mm-hays/bases',
    icone: BookOpen,
    permissao: 'mm_hays.bases_ver',
    grupo: GRUPO_ADMINISTRACAO,
  },
  /*
   * Modelos de IA (iteração 13.15): qual modelo atende cada uso. Administração, não dia a dia. A chave
   * `mm_hays.configurar` é de concessão do master (ADR 0026) e, além dela, o servidor exige SER o master —
   * então o item aparece para quem o master habilitou, e a troca ainda assim só o master consegue fazer.
   */
  {
    rotulo: 'Modelos de IA',
    dica: 'Qual modelo de IA atende a conversa do MM Hays e o resumo da demanda dos leads',
    para: '/mm-hays/modelos',
    icone: Cpu,
    permissao: 'mm_hays.configurar',
    grupo: GRUPO_ADMINISTRACAO,
  },
  /*
   * Listas de Leads e tipos de atividade (iteração 10.2): manutenção de opções, não trabalho do dia a dia —
   * por isso em "Administração". Cada um com a sua permissão, concedível pelo admin.
   */
  {
    rotulo: 'Listas de leads',
    dica: 'As opções que aparecem no cadastro de leads: origens, motivos de descarte e segmentos',
    para: '/leads/listas',
    icone: ListChecks,
    permissao: 'leads.listas',
    grupo: GRUPO_ADMINISTRACAO,
  },
  /*
   * Operações de crédito (iteração 14.1; ADR 0041): as 58 do guia saíram das listas de Leads e passaram ao
   * módulo Provedores. Fica em "Administração" porque é manutenção de catálogo, não trabalho do dia a dia —
   * o mesmo critério das listas de Leads e dos tipos de atividade.
   *
   * 🚨 O item aponta para `/provedores/operacoes`, e **não** para `/provedores`: o diretório nasceu na 14.4
   * como item próprio ("Provedores", solto, acima), e este continua aqui — manutenção de catálogo fechado,
   * pelo mesmo critério das listas de Leads e dos tipos de atividade.
   */
  {
    rotulo: 'Operações de crédito',
    dica: 'As 58 operações do guia que o cadastro de lead oferece e que cada provedor atende: OPEX, CAPEX e situações especiais',
    para: '/provedores/operacoes',
    icone: Landmark,
    permissao: 'provedores.ver',
    grupo: GRUPO_ADMINISTRACAO,
  },
  /*
   * O relatório da última carga dos anexos de pesquisa (14.11). Fica em "Administração" porque é operação,
   * não trabalho do dia a dia — e porque só quem tem `provedores.importar` precisa dele.
   *
   * 🚨 A carga **não** roda por esta tela: é script no servidor. O item leva ao relatório, que é onde as
   * divergências e os possíveis duplicados pedem decisão humana (ADR 0044).
   */
  {
    rotulo: 'Importação de provedores',
    dica: 'O relatório da última carga dos anexos de pesquisa: fichas lidas, divergências e possíveis duplicados. A carga roda por script, no servidor',
    para: '/provedores/importacao',
    icone: FileStack,
    permissao: 'provedores.importar',
    grupo: GRUPO_ADMINISTRACAO,
  },
  {
    rotulo: 'Tipos de atividade',
    dica: 'Os tipos de atividade que se registram num lead (ligação, reunião, visita…)',
    para: '/tipos-atividade',
    icone: ListTodo,
    permissao: 'atividades.tipos',
    grupo: GRUPO_ADMINISTRACAO,
  },
];

/**
 * Filtra pelo que o usuário pode abrir. `tem` é injetado (e não lido de um hook) para a função ser
 * pura e testável sem React.
 */
export function itensVisiveis(tem: (permissao: Permissao) => boolean): ItemMenu[] {
  return ITENS_MENU.filter((item) => !item.permissao || tem(item.permissao));
}

/** Agrupa preservando a ordem de declaração: primeiro os soltos, depois cada grupo como surge. */
export function menuAgrupado(
  tem: (permissao: Permissao) => boolean,
): { grupo?: string; itens: ItemMenu[] }[] {
  const secoes: { grupo?: string; itens: ItemMenu[] }[] = [];
  for (const item of itensVisiveis(tem)) {
    const existente = secoes.find((secao) => secao.grupo === item.grupo);
    if (existente) existente.itens.push(item);
    else secoes.push({ grupo: item.grupo, itens: [item] });
  }
  return secoes;
}

/**
 * Qual item do menu acende para o caminho aberto: o de **prefixo mais longo**. Com o `NavLink` puro, `/leads/listas`
 * acendia "Leads" e "Listas de leads" juntos (os dois são prefixo); e `/leads/123` tem de acender "Leads". O `/`
 * só casa consigo mesmo.
 */
export function itemAtivo(caminho: string): string | undefined {
  let melhor: string | undefined;
  for (const { para } of ITENS_MENU) {
    const casa =
      para === '/' ? caminho === '/' : caminho === para || caminho.startsWith(`${para}/`);
    if (casa && (melhor === undefined || para.length > melhor.length)) melhor = para;
  }
  return melhor;
}
